import React, { useEffect, useRef } from 'react';
import { Field } from 'react-final-form';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  getPuertoRicoMunicipality,
  getPuertoRicoMunicipalityOptions,
  isPuertoRicoMunicipalityId,
  isValidPuertoRicoCoordinates,
  CUSTOMER_SERVICE_LOCATION_ADDRESS_MAX_LENGTH,
} from '../../util/puertoRicoMunicipalities';
import { composeValidators, maxLength, required } from '../../util/validators';
import { reverseGeocodePuertoRico } from '../../util/reverseGeocodingMapbox';

import FieldSelect from '../FieldSelect/FieldSelect';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import ServiceMap from '../Map/Map';

import css from './CustomerServiceLocationFields.module.css';

export const ADDRESS_TEXT_MAX_LENGTH = CUSTOMER_SERVICE_LOCATION_ADDRESS_MAX_LENGTH;
export const REVERSE_GEOCODING_DEBOUNCE_MS = 600;

const validateMunicipality = (allowedIds, requiredMessage, unavailableMessage) => value => {
  if (!value) return requiredMessage;
  if (!isPuertoRicoMunicipalityId(value)) return requiredMessage;
  return allowedIds.includes(value) ? undefined : unavailableMessage;
};

const validateOrigin = message => value =>
  isValidPuertoRicoCoordinates(value) ? undefined : message;
const trimRequired = message => value =>
  typeof value === 'string' && value.trim().length > 0 ? undefined : message;

const CustomerServiceLocationFields = props => {
  const {
    form,
    values,
    allowedMunicipalityIds = [],
    requireServiceAreaSummary = false,
    compactSpacing = false,
  } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const reverseGeocodingTimer = useRef(null);
  const reverseGeocodingSequence = useRef(0);
  const hasNormalizedAreas =
    allowedMunicipalityIds.length > 0 && allowedMunicipalityIds.every(isPuertoRicoMunicipalityId);
  const location = values?.customerServiceLocation || {};
  const municipality = getPuertoRicoMunicipality(location.municipalityId);

  useEffect(
    () => () => {
      if (reverseGeocodingTimer.current) clearTimeout(reverseGeocodingTimer.current);
      reverseGeocodingSequence.current += 1;
    },
    []
  );

  if (!hasNormalizedAreas) {
    return (
      <p className={css.unavailable}>
        <FormattedMessage id="CustomerServiceLocationFields.listingNeedsUpdate" />
      </p>
    );
  }

  const onMunicipalityChange = municipalityId => {
    const selected = getPuertoRicoMunicipality(municipalityId);
    form.change('customerServiceLocation.origin', selected?.center || null);
    form.change('customerServiceLocation.approximateMapAddress', undefined);
  };
  const onPinChange = origin => {
    form.change('customerServiceLocation.origin', origin);
    form.change('customerServiceLocation.approximateMapAddress', undefined);
    if (reverseGeocodingTimer.current) clearTimeout(reverseGeocodingTimer.current);
    const requestSequence = ++reverseGeocodingSequence.current;
    reverseGeocodingTimer.current = setTimeout(() => {
      reverseGeocodePuertoRico(origin, config.localization.locale).then(approximateMapAddress => {
        if (requestSequence !== reverseGeocodingSequence.current) return;
        if (approximateMapAddress) {
          form.change('customerServiceLocation.approximateMapAddress', approximateMapAddress);
        }
      });
    }, REVERSE_GEOCODING_DEBOUNCE_MS);
  };
  const pinRequired = intl.formatMessage({ id: 'CustomerServiceLocationFields.pinRequired' });

  return (
    <section className={compactSpacing ? css.compactRoot : css.root}>
      <FieldSelect
        id="customerServiceLocation.municipalityId"
        name="customerServiceLocation.municipalityId"
        label={intl.formatMessage({ id: 'CustomerServiceLocationFields.municipalityLabel' })}
        onChange={onMunicipalityChange}
        validate={validateMunicipality(
          allowedMunicipalityIds,
          intl.formatMessage({ id: 'CustomerServiceLocationFields.municipalityRequired' }),
          intl.formatMessage({ id: 'CustomerServiceLocationFields.municipalityUnavailable' })
        )}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'CustomerServiceLocationFields.municipalityPlaceholder' })}
        </option>
        {getPuertoRicoMunicipalityOptions(allowedMunicipalityIds).map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </FieldSelect>

      {municipality ? (
        <div className={css.mapSection}>
          <h3 className={css.heading}>
            <FormattedMessage id="CustomerServiceLocationFields.mapTitle" />
          </h3>
          <p>
            <FormattedMessage id="CustomerServiceLocationFields.mapInstruction" />
          </p>
          <ServiceMap
            key={municipality.id}
            className={css.map}
            center={
              isValidPuertoRicoCoordinates(location.origin) ? location.origin : municipality.center
            }
            zoom={Math.max(municipality.zoom, 13)}
            mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
            markerDraggable
            onCenterChange={onPinChange}
            mapsConfig={{
              ...config.maps,
              fuzzy: {
                ...config.maps.fuzzy,
                enabled: false,
                defaultZoomLevel: Math.max(municipality.zoom, 13),
              },
            }}
          />
          <p className={css.help}>
            <FormattedMessage id="CustomerServiceLocationFields.mapHelp" />
          </p>
          <Field
            name="customerServiceLocation.origin"
            validate={validateOrigin(pinRequired)}
            component={() => null}
          />
        </div>
      ) : (
        <p className={css.instruction}>
          <FormattedMessage id="CustomerServiceLocationFields.selectFirst" />
        </p>
      )}

      {requireServiceAreaSummary ? (
        <div className={css.summaryFields}>
          <FieldTextInput
            id="customerServiceLocation.neighborhood"
            name="customerServiceLocation.neighborhood"
            type="text"
            label={intl.formatMessage({ id: 'CustomerServiceLocationFields.neighborhoodLabel' })}
            validate={trimRequired(
              intl.formatMessage({ id: 'CustomerServiceLocationFields.neighborhoodRequired' })
            )}
          />
          <FieldTextInput
            id="customerServiceLocation.postalCode"
            name="customerServiceLocation.postalCode"
            type="text"
            label={intl.formatMessage({ id: 'CustomerServiceLocationFields.postalCodeLabel' })}
            validate={value =>
              /^00[679]\d{2}(?:-\d{4})?$/.test(value?.trim?.() || '')
                ? undefined
                : intl.formatMessage({ id: 'CustomerServiceLocationFields.postalCodeRequired' })
            }
          />
          <FieldTextInput
            id="customerServiceLocation.generalReference"
            name="customerServiceLocation.generalReference"
            type="text"
            label={intl.formatMessage({ id: 'CustomerServiceLocationFields.generalReferenceLabel' })}
            placeholder={intl.formatMessage({
              id: 'CustomerServiceLocationFields.generalReferencePlaceholder',
            })}
            validate={trimRequired(
              intl.formatMessage({ id: 'CustomerServiceLocationFields.generalReferenceRequired' })
            )}
          />
          <p className={css.help}>
            <FormattedMessage id="CustomerServiceLocationFields.generalReferenceHelp" />
          </p>
        </div>
      ) : null}

      <FieldTextInput
        id="customerServiceLocation.addressText"
        name="customerServiceLocation.addressText"
        type="textarea"
        label={intl.formatMessage({ id: 'CustomerServiceLocationFields.addressLabel' })}
        placeholder={intl.formatMessage({ id: 'CustomerServiceLocationFields.addressPlaceholder' })}
        maxLength={ADDRESS_TEXT_MAX_LENGTH}
        validate={composeValidators(
          trimRequired(intl.formatMessage({ id: 'CustomerServiceLocationFields.addressRequired' })),
          maxLength(
            intl.formatMessage(
              { id: 'CustomerServiceLocationFields.addressTooLong' },
              { maxLength: ADDRESS_TEXT_MAX_LENGTH }
            ),
            ADDRESS_TEXT_MAX_LENGTH
          )
        )}
      />
      <p className={css.help}>
        <FormattedMessage id="CustomerServiceLocationFields.addressHelp" />
      </p>
    </section>
  );
};

export default CustomerServiceLocationFields;
